import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  Divider,
  Chip,
  Box,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useGetResponsesForAttemptQuery, useGetQuizQuestionsQuery } from '../../services/SkillsManagement';

/**
 * SeeMoreQuizEvaluation
 * Props:
 * - open: boolean
 * - onClose: function
 * - attemptId: number (required)
 * - quizId: number (required) -> used to fetch questions/options
 */
const SeeMoreQuizEvaluation = ({ open, onClose, attemptId, quizId }) => {
  const { data: respData, isLoading: loadingResp } = useGetResponsesForAttemptQuery(
    Number.isFinite(Number(attemptId)) ? { attemptId: Number(attemptId) } : { attemptId: 0 },
    { skip: !Number.isFinite(Number(attemptId)) }
  );

  const { data: qData, isLoading: loadingQ } = useGetQuizQuestionsQuery(
    { id: Number(quizId) },
    { skip: !Number.isFinite(Number(quizId)) || Number(quizId) <= 0 }
  );

  const questions = React.useMemo(() => {
    const arr = qData?.data?.questions || qData?.questions || [];
    return Array.isArray(arr)
      ? arr.map((q, i) => ({
          id: q?.questionId ?? q?.id,
          text: q?.questionText || '',
          type: q?.questionType || 'single_choice',
          options: (Array.isArray(q?.options) ? q.options : []).map((o, idx) => ({
            id: o?.questionOptionId ?? o?.optionId ?? o?.id ?? `${i}-${idx}`,
            text: o?.optionText || '',
            isCorrect: Boolean(o?.isCorrect),
          })),
        }))
      : [];
  }, [qData]);

  const responses = React.useMemo(() => {
    const arr = respData?.data || respData || [];
    return Array.isArray(arr) ? arr : [];
  }, [respData]);

  const merged = React.useMemo(() => {
    const byQ = new Map();
    for (const r of responses) {
      const qid = Number(r?.questionId);
      if (!byQ.has(qid)) byQ.set(qid, new Set());
      const set = byQ.get(qid);
      if (r?.optionId != null) set.add(Number(r.optionId));
    }
    return questions.map((q) => {
      const userSet = byQ.get(Number(q.id)) || new Set();
      const userIds = Array.from(userSet);
      const correctIds = q.options.filter((o) => o.isCorrect).map((o) => Number(o.id));
      const isCorrect = (() => {
        if (correctIds.length === 0) return false;
        const a = new Set(correctIds);
        const b = new Set(userIds);
        if (a.size !== b.size) return false;
        for (const v of a) if (!b.has(v)) return false;
        return true;
      })();
      const fmt = (ids) =>
        ids
          .map((id) => q.options.find((o) => Number(o.id) === Number(id))?.text)
          .filter(Boolean)
          .join(', ');
      return {
        id: q.id,
        text: q.text,
        userAnswerText: fmt(userIds),
        correctAnswerText: fmt(correctIds),
        isCorrect,
      };
    });
  }, [questions, responses]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Review Answers</DialogTitle>
      <DialogContent dividers>
        {loadingResp || loadingQ ? (
          <Typography variant="body2">Loading...</Typography>
        ) : merged.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No answers to show.</Typography>
        ) : (
          <Stack spacing={1.5}>
            {merged.map((item, idx) => (
              <Box key={item.id} sx={{ p: 0 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
                  <Typography variant="subtitle1" sx={{ flex: 1, fontWeight: 700 }}>{item.text}</Typography>
                  {item.isCorrect ? (
                    <Chip size="small" color="success" icon={<CheckCircleIcon />} label="Correct" />
                  ) : (
                    <Chip size="small" color="error" icon={<CancelIcon />} label="Incorrect" />
                  )}
                </Stack>
                <Box sx={{
                  p: 1.2,
                  borderRadius: 2,
                  bgcolor: item.isCorrect ? 'rgba(46, 125, 50, 0.08)' : 'rgba(198, 40, 40, 0.08)',
                  border: `1px solid ${item.isCorrect ? 'rgba(46,125,50,0.25)' : 'rgba(198,40,40,0.25)'}`,
                  color: item.isCorrect ? 'success.main' : 'error.main',
                }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {item.isCorrect ? (
                      <CheckCircleIcon fontSize="small" color="success" />
                    ) : (
                      <CancelIcon fontSize="small" color="error" />
                    )}
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Your answer:</Typography>
                    <Typography variant="body2" sx={{ color: 'inherit' }}>{item.userAnswerText || '-'}</Typography>
                  </Stack>
                </Box>
                {!item.isCorrect && (
                  <Box sx={{
                    mt: 1,
                    p: 1.2,
                    borderRadius: 2,
                    bgcolor: 'rgba(46, 125, 50, 0.08)',
                    border: '1px solid rgba(46,125,50,0.25)',
                    color: 'success.main',
                  }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CheckCircleIcon fontSize="small" color="success" />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Correct answer:</Typography>
                      <Typography variant="body2" sx={{ color: 'inherit' }}>{item.correctAnswerText || '-'}</Typography>
                    </Stack>
                  </Box>
                )}
                {idx < merged.length - 1 && <Divider sx={{ mt: 2, mb: 1.5 }} />}
              </Box>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SeeMoreQuizEvaluation;

